import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { useQueryClient } from 'react-query';
import { StyledButtonText, StyledDescription, StyledModalContainer } from 'components/Modal/StyledComponents';
import i18n from 'i18n';
import { MODAL_TYPES } from 'constants/modal-types';
import { resetUserData } from 'actions/app';
import { showLoader, hideLoader } from 'actions/loader';
import { hideModal, showModal } from 'actions/modal';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import usePinProtectModal from 'pages/Profile/Packages/usePinProtectModal';
import {
  StyledActionsWrapper,
  StyledButton,
  StyledButtonsArea,
  StyledPrice,
  StyledStatus,
  StyledTitle,
} from 'components/Modal/PackageModal/StyledComponents';
import useDeactivatePackage from 'hooks/data/useDeactivatePackage';
import { getContractNumberSelector } from 'reducers/profile/selectors';

const PackageModal = ({ selectedPackage, onModalBack }) => {
  const dispatch = useDispatch();
  const isPurchaseProtected = useSelector((state) => get(state, ['parental', 'data', 'purchase']));
  const queryClient = useQueryClient();
  const showPinProtect = usePinProtectModal();
  const contractCode = useSelector(getContractNumberSelector);
  const { name: title, cost, unit, status, isAdditional: isAdditionalPkg } = selectedPackage;

  const { mutate: deactivate } = useDeactivatePackage({
    onMutate: () => dispatch(showLoader()),
    onSettled: () => dispatch(hideLoader()),
    onSuccess: ({ message }) => {
      const handler = () => {
        onModalBack();
        dispatch(resetUserData());
        queryClient.resetQueries();
      };
      dispatch(
        showModal(MODAL_TYPES.NOTIFICATION, {
          onModalBack: handler,
          title: i18n.t('packages.successDeactivationModal.title'),
          description: message || i18n.t('packages.successDeactivationModal.message'),
          buttons: [{ text: i18n.t('packages.successDeactivationModal.close'), handler }],
        }),
      );
    },
    onError: (error, pkg) => {
      innerAnalyticsVolia.postEvent({
        eventGroup: 'terminate SVOD',
        eventName: 'additional',
        eventValue: `package code="${pkg.id}"; ${get(error, 'response.status', 'error')}`,
      });

      if (get(error, 'response.status') === 404) {
        dispatch(
          showModal(MODAL_TYPES.NOTIFICATION, {
            onModalBack: onModalBack,
            title: i18n.t('packages.notfoundDeactivationModal.title'),
            description: i18n.t('packages.notfoundDeactivationModal.message'),
            buttons: [
              {
                text: i18n.t('packages.notfoundDeactivationModal.repeat'),
                handler: () => {
                  onModalBack();
                  onDeactivation(pkg);
                },
              },
              {
                text: i18n.t('packages.notfoundDeactivationModal.close'),
                handler: onModalBack,
              },
            ],
          }),
        );
      } else {
        const message = error.message;
        dispatch(showModal(MODAL_TYPES.INFO, { onModalBack, message }));
      }
    },
  });

  const onDeactivation = useCallback(() => {
    const pkg = selectedPackage;

    innerAnalyticsVolia.postEvent({
      eventGroup: 'subscription management',
      eventName: `click disconnect`,
      eventValue: `package code="${pkg.id}"`,
    });

    const deactivateWrapper = () => {
      dispatch(hideModal());
      deactivate({ pkg, contractCode });
    };

    if (isPurchaseProtected) {
      showPinProtect(deactivateWrapper, onModalBack);
    } else {
      deactivateWrapper();
    }
  }, [contractCode, deactivate, dispatch, isPurchaseProtected, onModalBack, selectedPackage, showPinProtect]);

  return (
    <StyledModalContainer>
      <StyledTitle>{title}</StyledTitle>
      {status && <StyledStatus>{status}</StyledStatus>}
      <StyledPrice>
        {cost} {unit}
      </StyledPrice>
      {!isAdditionalPkg && <StyledDescription>{i18n.t(`packages.modal.changeMain`)}</StyledDescription>}
      <StyledActionsWrapper
        config={{
          leaveFor: {
            right: '',
            up: '',
            left: '',
            down: '',
          },
        }}
        defaultSection
        isAdditionalPkg={isAdditionalPkg}
      >
        <StyledButtonsArea>
          {isAdditionalPkg && (
            <StyledButton onEnter={onDeactivation}>
              <StyledButtonText>{i18n.t(`packages.modal.deactivate`)}</StyledButtonText>
            </StyledButton>
          )}
          <StyledButton onEnter={onModalBack}>
            <StyledButtonText>{i18n.t(`packages.modal.close`)}</StyledButtonText>
          </StyledButton>
        </StyledButtonsArea>
      </StyledActionsWrapper>
    </StyledModalContainer>
  );
};

export default PackageModal;
